/**
 * Process-wide pending-mutations tracker (Phase 5).
 *
 * One tracker per session. Lazy init so tests can construct fresh
 * trackers via `createPendingMutationsTracker` without the singleton
 * leaking state across runs.
 *
 * The online detector arms / disarms the tracker on connection state
 * changes — see `online-detector.ts` integration in `use-user-do-sync.ts`.
 *
 * The tracker's timeout hook now also pops the matching pre-image
 * from the stash and applies its revert thunk — so a mutation that
 * never received an ack inside the timeout window is treated the
 * same as a loud `op-rejected`.
 */

import { createPendingMutationsTracker, type Tracker } from './pending-mutations';
import { getPreImageStash } from './pre-image-stash';

let tracker:
  | (Tracker & {
      arm(): void;
      disarm(): void;
      isArmed(): boolean;
    })
  | null = null;

export function getPendingMutationsTracker() {
  if (!tracker) {
    tracker = createPendingMutationsTracker({
      onTimeout: (mutationId, outcome) => {
        // Phase E: a silent timeout means no scope acked inside the
        // window. Revert the optimistic write so the UI doesn't
        // leave a stale local edit hanging.
        getPreImageStash().consume(mutationId)?.revert();
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Sync] mutation timed out — reverted pre-image:', {
            mutationId,
            outcome,
          });
        }
      },
    });
  }
  return tracker;
}

/** Test-only: replace the singleton (e.g. with a deterministic clock). */
export function _setPendingMutationsTracker(
  next: ReturnType<typeof getPendingMutationsTracker>,
): void {
  tracker = next;
}

/** Test-only: reset the singleton. */
export function _resetPendingMutationsTracker(): void {
  tracker?.clear();
  tracker = null;
}
